import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import {
  CiscoMigrationSiteConfiguration,
  CiscoMigrationSiteSourceField,
  CiscoMigrationSiteSourceValues,
  CiscoMigrationSiteValueMapping,
} from '../../../shared/models/generated/ziroModels';

@Injectable()
export class MigrationsCiscoSourceFieldMappingContext {
  private _http = inject(HttpClient);
  private _route = inject(ActivatedRoute);

  private _sourceFieldSource = new ReplaySubject<CiscoMigrationSiteSourceField | null>(1);
  sourceField$ = this._sourceFieldSource.asObservable();

  private _sourceValuesSource = new ReplaySubject<CiscoMigrationSiteSourceValues>(1);
  sourceValues$ = this._sourceValuesSource.asObservable();

  private _mappingsSource = new ReplaySubject<CiscoMigrationSiteValueMapping[]>(1);
  mappings$ = this._mappingsSource.asObservable();

  private _baseUrl = `/services/microsoft/migrations/tenants/${this._route.snapshot.params.tenantId}/exports/cisco/${this._route.snapshot.params.id}/number-report/site-management`;

  constructor() {
    this.refreshAll().subscribe();
  }

  refreshAll(): Observable<void> {
    return forkJoin([this._refreshSourceField(), this._refreshSourceValues(), this._refreshMappings()]).pipe(
      map((): void => undefined),
    );
  }

  saveSourceField(sourceField: CiscoMigrationSiteSourceField): Observable<void> {
    return this._http
      .put<void>(`${this._baseUrl}/source-field`, { sourceField } as CiscoMigrationSiteConfiguration)
      .pipe(
        switchMap(() =>
          forkJoin([this._refreshSourceField(), this._refreshMappings()]).pipe(map((): void => undefined)),
        ),
      );
  }

  saveMappings(mappings: CiscoMigrationSiteValueMapping[]): Observable<void> {
    return this._http
      .put<void>(`${this._baseUrl}/site-mappings`, mappings)
      .pipe(switchMap(() => this._refreshMappings()));
  }

  private _refreshSourceField(): Observable<void> {
    return this._http.get<CiscoMigrationSiteConfiguration>(`${this._baseUrl}/source-field`).pipe(
      catchError((error: HttpErrorResponse): Observable<CiscoMigrationSiteConfiguration | null> => {
        if (error.status === 404) {
          return of(null);
        }
        throw error;
      }),
      map((configuration: CiscoMigrationSiteConfiguration | null): void => {
        this._sourceFieldSource.next(configuration?.sourceField ?? null);
      }),
    );
  }

  private _refreshSourceValues(): Observable<void> {
    return this._http.get<CiscoMigrationSiteSourceValues>(`${this._baseUrl}/source-values`).pipe(
      map((sourceValues: CiscoMigrationSiteSourceValues): void => {
        this._sourceValuesSource.next(sourceValues);
      }),
    );
  }

  private _refreshMappings(): Observable<void> {
    return this._http.get<CiscoMigrationSiteValueMapping[]>(`${this._baseUrl}/site-mappings`).pipe(
      map((mappings: CiscoMigrationSiteValueMapping[]): void => {
        this._mappingsSource.next(mappings ?? []);
      }),
    );
  }
}
